<section class="step" aria-labelledby="sb-upload-title">
  <!-- Always in the page so screen readers hear the reading and its result. -->
  <p class="bk-sr-only" role="status" aria-live="polite">{{ announcement }}</p>

  <div class="bk-panel panel">
    <header class="panel__head">
      <div class="panel__titles">
        <h2 class="bk-section-title panel__title" id="sb-upload-title" tabindex="-1">Upload your sheet</h2>
        <p class="panel__sub">
          One row per person, with column names in the first row. Every column becomes a field you can put in
          the message.
        </p>
      </div>
    </header>

    @if (store.awaitingFile) {
      <ul class="notes">
        <li class="note" data-tone="wait">
          <span>
            Upload <strong>{{ store.awaitingFile }}</strong> again to carry on.
            <span class="note__more">Your message and settings are kept and come back once the same columns load.</span>
          </span>
        </li>
      </ul>
    }

    @if (!store.hasRows() || loading) {
      <div
        class="drop"
        [class.is-over]="isHovering"
        [class.is-busy]="loading"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
      >
        @if (loading) {
          <span class="drop__icon bk-sym" aria-hidden="true">hourglass_top</span>
          <p class="drop__title">Reading {{ readingName }}…</p>
          <span class="bk-skel drop__skel" aria-hidden="true"></span>
        } @else {
          <span class="drop__icon bk-sym" aria-hidden="true">upload_file</span>
          <p class="drop__title">Drop a spreadsheet here</p>
          <p class="drop__sub">CSV, Excel or OpenDocument, up to 20 MB</p>
          <button type="button" class="bk-btn bk-btn--primary" (click)="choose()">
            <span class="bk-sym" aria-hidden="true">folder_open</span>
            Choose file
          </button>
        }
      </div>
    } @else {
      <div
        class="file"
        [class.is-over]="isHovering"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
      >
        <span class="file__icon bk-sym" aria-hidden="true">table_view</span>
        <div class="file__text">
          <p class="file__name" [attr.title]="store.fileNameSig()">{{ store.fileNameSig() }}</p>
          <p class="file__meta">
            {{ formatCount(store.rowsSig().length) }} {{ store.rowsSig().length === 1 ? "row" : "rows" }}
            <span class="file__sep" aria-hidden="true">·</span>
            {{ store.columns().length }} {{ store.columns().length === 1 ? "column" : "columns" }}
            @if (fileSize) {
              <span class="file__sep" aria-hidden="true">·</span>{{ fileSize }}
            }
          </p>
        </div>
        <button type="button" class="bk-btn bk-btn--ghost" (click)="choose()">
          <span class="bk-sym" aria-hidden="true">swap_horiz</span>
          Replace file
        </button>
      </div>

      @if (store.canPickSheet) {
        <div class="bk-field sheet">
          <label class="bk-field__label" for="sb-sheet">Sheet</label>
          <select id="sb-sheet" class="bk-field__input" (change)="onSheetChange($event)">
            @for (name of store.sheetNames(); track $index; let i = $index) {
              <option [value]="name" [selected]="name === store.sheetName()">{{ sheetLabel(i) }}</option>
            }
          </select>
          <p class="bk-field__hint">
            This file has {{ store.sheetNames().length }} sheets. Pick the one that holds your list.
          </p>
        </div>
      }

      <ul class="notes" aria-label="What we found in the file">
        @for (note of notes(); track $index) {
          <li class="note" [attr.data-tone]="note.tone">
            <span>
              {{ note.text }}
              @if (note.more) {
                <span class="note__more">{{ note.more }}</span>
              }
            </span>
          </li>
        }
      </ul>
    }

    <input
      #picker
      type="file"
      class="bk-sr-only"
      tabindex="-1"
      aria-hidden="true"
      [accept]="acceptedFormats"
      (change)="onFileSelected($event)"
    />

    @if (errorMessage) {
      <p class="sb-error" role="alert">
        <span class="bk-sym" aria-hidden="true">error</span>
        <span>{{ errorMessage }}</span>
      </p>
    }
  </div>

  <footer class="step__foot">
    <span></span>
    <div class="step__foot-end">
      @if (!canContinue) {
        <span class="step__why" id="sb-upload-why">
          {{ loading ? "Reading the file…" : "Upload a sheet first." }}
        </span>
      }
      <button
        type="button"
        class="bk-btn bk-btn--primary"
        [attr.aria-disabled]="!canContinue || null"
        [attr.aria-describedby]="canContinue ? null : 'sb-upload-why'"
        (click)="continue()"
      >
        Choose the phone column
        <span class="bk-sym trail" aria-hidden="true">arrow_forward</span>
      </button>
    </div>
  </footer>
</section>
